import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Trash2, ShieldAlert, Loader2, Database } from 'lucide-react';
import { 
    collection, query, where, getDocs, writeBatch, doc, deleteDoc, getDoc, limit, setDoc, serverTimestamp
} from 'firebase/firestore';
import { signOut, deleteUser } from 'firebase/auth';
import { db, auth } from '../../firebase';

const DeleteAccountPage = () => {
    const navigate = useNavigate();
    const user = auth.currentUser;

    // Stages: 'initial' -> 'gathering' -> 'summary' -> 'deleting'
    const [stage, setStage] = useState('initial');

    // Modal State
    const [modalVisible, setModalVisible] = useState(true);
    const [modalType, setModalType] = useState('gather_input'); // 'gather_input' | 'delete_confirm'
    const [inputText, setInputText] = useState('');
    const [errorText, setErrorText] = useState('');
    const [deleteReason, setDeleteReason] = useState('');

    // Stats State
    const [stats, setStats] = useState({
        comments: 0,
        confessions: 0,
        postComments: 0,
        posts: 0,
        username: '',
        chats: 0,
        followers: 0,
        following: 0
    });

    // Progress State
    const [progressStatus, setProgressStatus] = useState('');
    const [progressPercent, setProgressPercent] = useState(0);
    const progressRef = useRef(null);

    // --- Helper: Batch Delete (Iterative & Safe) ---
    const safeDeleteBatch = async (queryRef) => {
        const BATCH_SIZE = 450;
        
        while (true) {
            const limitedQ = query(queryRef, limit(BATCH_SIZE));
            const snapshot = await getDocs(limitedQ);
            
            if (snapshot.empty) break;

            const batch = writeBatch(db);
            snapshot.docs.forEach((docSnap) => {
                batch.delete(docSnap.ref);
            });
            
            await batch.commit();
            
            if (snapshot.size < BATCH_SIZE) break;
        }
    };

    // Animation helper
    const updateProgress = (toValue) => {
        setProgressPercent(Math.round(toValue * 100));
    };

    // --- Logic: Gather Info ---
    const startGathering = async () => {
        if (inputText !== 'Gather account info') {
            setErrorText('Text does not match exactly.');
            return;
        }

        setModalVisible(false);
        setInputText('');
        setErrorText('');
        setStage('gathering');

        try {
            if (!user) throw new Error('No user found');
            const uid = user.uid;

            // Step A: Username
            updateProgress(0.1);
            setProgressStatus('Analyzing your identity...');
            const userDoc = await getDoc(doc(db, 'users', uid));
            const username = userDoc.exists() ? userDoc.data().username : 'Unknown';

            // Step B: Comments
            updateProgress(0.3);
            setProgressStatus('Scanning shadow comments...');
            const commentsQ = query(collection(db, 'comments'), where('userId', '==', uid));
            const commentsSnap = await getDocs(commentsQ);

            // Step C: Confessions
            updateProgress(0.5);
            setProgressStatus('Locating your shadows...');
            const confQ = query(collection(db, 'confessions'), where('userId', '==', uid));
            const confSnap = await getDocs(confQ);

            // Step D: Post Comments
            updateProgress(0.7);
            setProgressStatus('Scanning spark post comments...');
            const postCommentsQ = query(collection(db, 'postcomments'), where('userId', '==', uid));
            const postCommentsSnap = await getDocs(postCommentsQ);

            // Step E: Spark Posts
            updateProgress(0.9);
            setProgressStatus('Checking social impact...');
            const postsQ = query(collection(db, 'sparkposts'), where('uid', '==', uid));
            const postsSnap = await getDocs(postsQ);

            // Step F: Anonymous Chats & Real-time Stats
            updateProgress(0.95);
            setProgressStatus('Checking social connections...');
            const anonChatsQ = query(collection(db, 'anonymousChats'), where('participants', 'array-contains', uid));
            const anonChatsSnap = await getDocs(anonChatsQ);

            const userData = userDoc.exists() ? userDoc.data() : {};

            // Finish
            setStats({
                comments: commentsSnap.size,
                confessions: confSnap.size,
                postComments: postCommentsSnap.size,
                posts: postsSnap.size,
                username: username,
                chats: anonChatsSnap.size,
                followers: userData.followersCount || 0,
                following: userData.followingCount || 0
            });

            updateProgress(1);
            setProgressStatus('Analysis Complete.');
            setTimeout(() => setStage('summary'), 800);

        } catch (err) {
            console.error("Gathering Error:", err);
            toast.error('Could not gather info. Please check connection.');
            navigate(-1);
        }
    };

    // --- Logic: Delete Account ---
    const startDeletion = async () => {
        if (inputText !== 'Delete my account') {
            setErrorText('Text does not match exactly.');
            return;
        }

        setModalVisible(false);
        setStage('deleting');
        setProgressPercent(0);

        try {
            const uid = user.uid;

            // 1. Delete Comments
            setProgressStatus('Deleting your shadow comments...');
            updateProgress(0.10);
            const commentsQ = query(collection(db, 'comments'), where('userId', '==', uid));
            await safeDeleteBatch(commentsQ);

            // 2. Delete Confessions
            setProgressStatus('Erasing your shadows...');
            updateProgress(0.25);
            const confQ = query(collection(db, 'confessions'), where('userId', '==', uid));
            await safeDeleteBatch(confQ);

            // 3. Delete Post Comments
            setProgressStatus('Removing comments on posts...');
            updateProgress(0.40);
            const pcQ = query(collection(db, 'postcomments'), where('userId', '==', uid));
            await safeDeleteBatch(pcQ);

            // 4. Delete Spark Posts
            setProgressStatus('Deleting your spark posts...');
            updateProgress(0.55);
            const postsQ = query(collection(db, 'sparkposts'), where('uid', '==', uid));
            await safeDeleteBatch(postsQ);

            // 4.5. THE SIGNAL - Cloud Function handles cross-user cleanup
            setProgressStatus('Triggering background cleanup...');
            updateProgress(0.65);

            const userProfileSnap = await getDoc(doc(db, 'users', uid));
            const userData = userProfileSnap.exists() ? userProfileSnap.data() : {};

            await setDoc(doc(db, 'deletedAccounts', uid), {
                uid: uid,
                email: user.email || 'No Email Linked',
                username: userData.username || 'Unknown',
                deletedAt: serverTimestamp(),
                reason: deleteReason.trim() || 'User Requested'
            });

            // 5. Delete Username Reservation
            setProgressStatus('Releasing username...');
            updateProgress(0.75);
            if (stats.username) {
                await deleteDoc(doc(db, 'usernames', stats.username)).catch(e => console.log('Username delete issue', e));
            }

            // 6. Delete User Subcollections
            setProgressStatus('Cleaning up user data...');
            updateProgress(0.85);
            const subcollections = [
                'Streak', 'confShares', 'followers', 'following',
                'likedConf', 'likedPosts', 'notifications', 'postShares',
                'savedPosts', 'settings', 'votes'
            ];
            for (const sub of subcollections) {
                const subRef = collection(db, 'users', uid, sub);
                await safeDeleteBatch(subRef);
            }

            // 7. Delete User Document
            setProgressStatus('Finalizing deletion...');
            updateProgress(0.95);
            await deleteDoc(doc(db, 'users', uid));

            // 8. Delete Auth
            setProgressStatus('Goodbye.');
            updateProgress(1.0);
            
            await deleteUser(user);

        } catch (err) {
            console.error("Deletion Error:", err);

            if (err.code === 'auth/requires-recent-login' || err.code === 'auth/user-not-found') {
                await signOut(auth);
            } else {
                toast.error('An error occurred during deletion. Please try again.');
                navigate(-1);
            }
        }
    };

    // Calculate Total Activity for Summary View
    const hasActivity = (stats.posts + stats.confessions + stats.comments + stats.postComments + stats.chats + stats.followers) > 0;

    // --- RENDER ---
    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4">
                <button onClick={() => navigate('/settings')} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                    <ChevronLeft size={24} className="text-white" />
                </button>
                <h1 className="text-xl font-bold text-white">Delete Account</h1>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 sm:pb-0 flex flex-col items-center justify-center">

                {/* STAGE: GATHERING ANIMATION */}
                {stage === 'gathering' && (
                    <div className="flex flex-col items-center justify-center w-full max-w-md">
                        <div className="animate-spin mb-5">
                            <Loader2 size={60} className="text-[#FF453A]" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mt-5 mb-2">Gathering Data</h2>
                        
                        {/* Progress Bar */}
                        <div className="w-full mt-8">
                            <div className="flex justify-end mb-1">
                                <span className="text-[#FF453A] font-bold">{progressPercent}%</span>
                            </div>
                            <div className="w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-[#FF453A] rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="text-gray-500 text-sm text-center mt-3">{progressStatus}</p>
                        </div>
                    </div>
                )}

                {/* STAGE: SUMMARY */}
                {stage === 'summary' && (
                    <div className="w-full max-w-md bg-[#161616] border border-[#222] rounded-2xl p-6">
                        <div className="flex justify-center mb-5">
                            <ShieldAlert size={60} className="text-[#FF453A]" />
                        </div>
                        <h2 className="text-2xl font-bold text-white text-center mb-3">Are you absolutely sure?</h2>
                        <p className="text-gray-400 text-sm text-center mb-6 leading-relaxed">
                            We have gathered all the information related to your account and interactions.
                            {hasActivity ? (
                                <><br /><br />We also found the following things. Upon deletion, ALL this data will be permanently wiped. You will lose everything listed below.</>
                            ) : (
                                <><br /><br />Upon deletion, ALL your data will be permanently wiped. You will lose everything.</>
                            )}
                        </p>

                        {hasActivity && (
                            <>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.followers}</p>
                                        <p className="text-xs text-gray-500">Followers</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.following}</p>
                                        <p className="text-xs text-gray-500">Following</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.chats}</p>
                                        <p className="text-xs text-gray-500">Anon Chats</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.posts}</p>
                                        <p className="text-xs text-gray-500">Posts</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.confessions}</p>
                                        <p className="text-xs text-gray-500">Shadows</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-white">{stats.comments + stats.postComments}</p>
                                        <p className="text-xs text-gray-500">Comments</p>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="h-[1px] bg-[#2a2a2a] my-5" />

                        {/* Reason Input */}
                        <div className="mb-5">
                            <label className="text-gray-500 text-xs mb-2 block ml-1">Reason for leaving (Optional)</label>
                            <textarea 
                                className="w-full bg-[#222] text-white rounded-xl p-3 text-sm outline-none resize-none min-h-[80px] border border-transparent focus:border-[#FF453A] transition-colors"
                                placeholder="Why are you deleting your account?"
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                maxLength={200}
                            />
                        </div>

                        <button 
                            onClick={() => {
                                setModalType('delete_confirm');
                                setModalVisible(true);
                                setErrorText('');
                                setInputText('');
                            }}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF453A] text-white font-bold py-4 rounded-xl hover:bg-[#FF453A]/80 transition-colors shadow-[0_4px_20px_rgba(255,69,58,0.3)]"
                        >
                            <Trash2 size={20} /> Delete My Account
                        </button>
                    </div>
                )}

                {/* STAGE: DELETING ANIMATION */}
                {stage === 'deleting' && (
                    <div className="flex flex-col items-center justify-center w-full max-w-md">
                        <div className="animate-spin mb-5">
                            <Database size={60} className="text-[#FF453A]" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#FF453A] mt-5 mb-2">Deleting Data</h2>
                        <p className="text-gray-400 text-sm mb-6">Do not close the browser.</p>
                        
                        {/* Progress Bar */}
                        <div className="w-full mt-4">
                            <div className="flex justify-end mb-1">
                                <span className="text-[#FF453A] font-bold">{progressPercent}%</span>
                            </div>
                            <div className="w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-[#FF453A] rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="text-gray-500 text-sm text-center mt-3">{progressStatus}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* INPUT MODAL OVERLAY */}
            {modalVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-white">
                                {modalType === 'gather_input' ? 'Verification Step 1' : 'Final Confirmation'}
                            </h3>
                            <button 
                                onClick={() => {
                                    if (modalType === 'gather_input') navigate(-1);
                                    else setModalVisible(false);
                                }}
                                className="text-gray-500 hover:text-white transition-colors text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>

                        <p className="text-gray-400 text-sm text-center mb-4">
                            {modalType === 'gather_input'
                                ? 'To prevent accidental deletion, please type exactly:'
                                : 'This cannot be undone. To confirm, type exactly:'}
                        </p>

                        <div className="bg-black border border-[#333] rounded-lg py-2 px-4 mb-5 text-center">
                            <span className="text-white font-bold text-base">
                                {modalType === 'gather_input' ? 'Gather account info' : 'Delete my account'}
                            </span>
                        </div>

                        <input 
                            type="text"
                            className={`w-full bg-[#222] text-white rounded-xl p-4 text-sm outline-none mb-2 border ${errorText ? 'border-[#FF453A]' : 'border-transparent'} focus:border-[#FF453A] transition-colors`}
                            placeholder="Type here..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                        />
                        {errorText && <p className="text-[#FF453A] text-xs mb-2">{errorText}</p>}

                        <button 
                            onClick={modalType === 'gather_input' ? startGathering : startDeletion}
                            className="w-full bg-[#FF453A] text-white font-bold py-3.5 rounded-xl mt-3 hover:bg-[#FF453A]/80 transition-colors"
                        >
                            {modalType === 'gather_input' ? 'Start Process' : 'Confirm Delete'}
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
};

export { DeleteAccountPage };
export default DeleteAccountPage;
